import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserCounts } from '@/contexts/UserCountsContext';
import { Gavel, Heart, ShoppingBag, Package, Users } from 'lucide-react';
import './UserTabBar.css';

const tabs = [
    { id: 'participating', label: 'Licitații', icon: Gavel, path: '/dashboard?tab=participating', countKey: 'participating' },
    { id: 'buying', label: 'Cumpărate', icon: ShoppingBag, path: '/dashboard?tab=buying', countKey: 'buying' },
    { id: 'sold', label: 'Vândute', icon: Package, path: '/dashboard?tab=sold', countKey: 'sold' },
    { id: 'for-sale', label: 'De vânzare', icon: Package, path: '/dashboard?tab=for-sale', countKey: 'forSale' },
    { id: 'favorites', label: 'Apreciate', icon: Heart, path: '/dashboard?tab=favorites', countKey: 'favorites' },
    { id: 'following', label: 'Urmăriți', icon: Users, path: '/dashboard?tab=following', countKey: 'following' },
];

export const UserTabBar = () => {
    const { isAuthenticated } = useAuth();
    const { counts, loading } = useUserCounts();
    const navigate = useNavigate();
    const location = useLocation();
    const [isVisible, setIsVisible] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            
            // Show when scrolling up or at top, hide when scrolling down
            if (currentScrollY < 100) {
                setIsVisible(true);
            } else if (currentScrollY < lastScrollY) {
                setIsVisible(true);
            } else if (currentScrollY > lastScrollY) {
                setIsVisible(false);
            }
            
            setLastScrollY(currentScrollY);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScrollY]);

    if (!isAuthenticated) return null;

    // Determine active tab from current URL
    const searchParams = new URLSearchParams(location.search);
    const currentTab = location.pathname === '/dashboard' ? (searchParams.get('tab') || 'for-sale') : null;

    // Hide UserTabBar on these tabs
    const hiddenTabs = ['buying', 'sold', 'for-sale', 'following', 'favorites'];
    if (currentTab && hiddenTabs.includes(currentTab)) {
        return null;
    }

    const handleTabClick = (tab) => {
        navigate(tab.path);
    };

    return (
        <div 
            className={`hidden lg:block sticky top-[56px] z-40 pointer-events-none transition-all duration-300 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full'
            }`} 
            data-testid="user-tab-bar"
        >
            <div className="container mx-auto px-4 flex justify-center overflow-x-auto">
                <div className="user-tabbar-container bg-white/90 backdrop-blur-xl rounded-b-3xl p-1.5 inline-flex flex-wrap gap-1 pointer-events-auto border-x border-b shadow-xs -mt-[1px]">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = currentTab === tab.id;
                        const count = counts[tab.countKey] || 0;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => handleTabClick(tab)}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                                    isActive
                                        ? 'bg-primary text-white'
                                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                }`}
                            >
                                <Icon className="w-4 h-4" />
                                <span className="hidden sm:inline">{tab.label}</span>
                                <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                                    isActive ? 'bg-white/20 text-white' : 'bg-primary/10 text-primary'
                                }`}>{count}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default UserTabBar;
